import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";

import { clearAllFilters } from "@dashboard/devices/store";
import { Box, CheckboxProps, Typography } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { ControlledSelectValue } from "@shared/form/SelectField/SelectField.types";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useAllWorkspaces, useCurrentWorkspace } from "../../api/helpers";
import { SubscriptionAlert } from "../SubscriptionAlert/SubscriptionAlert";

import { useStyles } from "./WorkspaceSelector.style";

type WorkspaceSelectorProps = {
    className?: string;
};

export function WorkspaceSelector(props: WorkspaceSelectorProps) {
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { data: workspaces = [] } = useAllWorkspaces();
    const [includeTerminatedWorkspaces, setIncludeTerminatedWorkspaces] = useState(() =>
        UserSettingsService.getIncludeTerminatedWorkspaces(),
    );

    const handleWorkspaceChange = (value: ControlledSelectValue<false> | undefined) => {
        dispatch(setCurrentWorkspaceId(value));
        dispatch(clearAllFilters());
    };

    const onTerminatedChange: CheckboxProps["onChange"] = (_e, checked) => {
        UserSettingsService.setCommonValue("includeTerminatedWorkspaces", checked);
        setIncludeTerminatedWorkspaces(checked);
    };

    return (
        <Form
            initialValues={{ id: workspace?.id }}
            onSubmit={() => undefined}
            render={() => (
                <SectionPanel
                    title={t("common:label.workspaces")}
                    className={cx(classes.workspaceSelectorAboveOverlay, props.className)}
                >
                    <Typography variant="body1" className={classes.description}>
                        {t("pages:workspacePage.intro.about.description")}
                    </Typography>
                    <Box className={classes.root}>
                        <Box className={classes.selectorGroup}>
                            <Typography variant="subtitle1" fontWeight="bold" component="div">
                                {t("pages:workspacePage.section.title.activeWorkspace")}
                            </Typography>
                            <Box className={classes.selectWrap}>
                                <ControlledSelectField
                                    name="id"
                                    label={t("pages:workspacePage.input.currentWorkspace.caption")}
                                    onChange={handleWorkspaceChange}
                                    options={workspaces.map((item) => ({
                                        label: item.name,
                                        value: item.id,
                                    }))}
                                    searchable
                                />
                            </Box>
                        </Box>
                        <Box className={classes.checkboxWrap}>
                            <CheckboxField
                                checked={includeTerminatedWorkspaces}
                                label={t("pages:workspacePage.input.showTerminatedWorkspaces.caption")}
                                onChange={onTerminatedChange}
                            />
                        </Box>
                        <SubscriptionAlert />
                    </Box>
                </SectionPanel>
            )}
        />
    );
}
